import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useCallReports, CallOutcome } from "@/shared/hooks/useCallReports";
import { useToast } from "@/shared/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { apiClient } from "@/shared/services/api";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { getObjectUrl } from "@/lib/storageUrl";
import { VideoPlayerModal } from "@/shared/components/VideoPlayerModal";
import { ImageModal } from "@/shared/components/ImageModal";

// Detect if running in WebView
const isWebView = () => {
  const ua = navigator.userAgent;
  if (typeof (window as any).ReactNativeWebView !== 'undefined') return true;
  if (ua.includes('WlkthruMobileApp')) return true;
  if ((window as any).__IS_MOBILE_WEBVIEW__ === true) return true;
  return false;
};

export default function SessionSummaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { saveCallReport } = useCallReports();
  const { toast } = useToast();

  const [notes, setNotes] = useState("");
  const [snapshots, setSnapshots] = useState<
    Array<{ name: string; url: string }>
  >([]);
  const [loadingShots, setLoadingShots] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ name: string; url: string } | null>(null);

  // Analytics + AI + customer from DB
  const [analytics, setAnalytics] = useState<any>(null);
  const [aiSummary, setAiSummary] = useState<string[]>([]);
  const [aiTranscript, setAiTranscript] = useState<string>("");
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingPath, setRecordingPath] = useState<string | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [customerData, setCustomerData] = useState<{
    name: string;
    phone: string;
    email: string;
    project: string;
  } | null>(null);

  const callData = location.state?.callData;
  const roomId: string | undefined = callData?.roomId;
  const isHostView = callData?.isHost !== false;


  // WebView: Send call-ended message back to mobile app and close
  useEffect(() => {
    if (isWebView()) {
      console.log("[SessionSummaryPage] WebView detected - sending call-ended message to mobile app");

      // Send call data back to mobile app in the expected format
      // Mobile app expects: { type: "call-ended", data: { isHost: boolean, ... } }
      if ((window as any).ReactNativeWebView) {
        try {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "call-ended",
              data: {
                isHost: isHostView,
                roomId: roomId,
                ...callData,
              },
            })
          );
        } catch (e) {
          console.error("Error posting call-ended message:", e);
        }
      }
    }
  }, [roomId, callData, isHostView]);

  // WebView early return - show simple redirecting screen
  if (isWebView()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg font-medium text-foreground">Redirecting...</p>
          <p className="text-sm text-muted-foreground mt-2">Returning to the app</p>
        </div>
      </div>
    );
  }


  useEffect(() => {
    if (callData?.notes && !notes) {
      setNotes(callData.notes);
    }
  }, [callData, notes]);

  // ✅ Auto-save notes with debouncing (500ms delay) - Using backend API
  useEffect(() => {
    if (!roomId || !notes) return;

    const timer = setTimeout(async () => {
      try {
        // ✅ NEW: Use backend API instead of direct Supabase call
        await apiClient.patch(API_ROUTES.SESSION_NOTES_UPDATE(roomId), { notes });
        console.log("✅ Notes auto-saved via backend API");
      } catch (error) {
        console.error("Error auto-saving notes:", error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [notes, roomId]);

  // Load snapshots from storage
  useEffect(() => {
    const loadSnapshots = async () => {
      if (!roomId) return;
      setLoadingShots(true);
      try {
        const { data, error } = await supabase.storage
          .from("call-snapshots")
          .list(roomId, {
            sortBy: { column: "name", order: "desc" },
            limit: 100,
          });
        if (error) throw error;
        const items = (data || []).filter((i) => !!i.name);
        const files = items.filter((i) =>
          /\.(png|jpg|jpeg|webp)$/i.test(i.name)
        );

        // Use Promise.allSettled to fetch URLs independently
        const urlResults = await Promise.allSettled(
          files.map(async (item) => {
            const fullPath = `${roomId}/${item.name}`;
            const url = await getObjectUrl("call-snapshots", fullPath);
            return { name: item.name, url };
          })
        );

        // Extract successful results only
        const urls = urlResults
          .filter((result): result is PromiseFulfilledResult<{ name: string; url: string }> =>
            result.status === 'fulfilled' && !!result.value.url
          )
          .map(result => result.value);

        setSnapshots(urls);
      } catch (e) {
        console.error("Snapshot list error:", e);
      } finally {
        setLoadingShots(false);
      }
    };
    loadSnapshots();
  }, [roomId]);

  // SECURITY: Fetch analytics + AI + customer from backend API
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!roomId) return;
      try {
        const response = await apiClient.get<{
          ok: boolean;
          session: {
            id: string;
            ai_summary: any;
            ai_transcript: string;
            customer_name: string;
            customer_phone: string;
            customer_email: string;
            customer_project: string;
            recording_status: string;
            recording_url: string;
            notes: string;
            analytics: any;
            recording: { url: string; storage_path: string; status: string } | null;
          };
        }>(`/sessions/${roomId}/summary`);

        if (!response.ok || !response.session) {
          console.error("Error fetching call session for analytics");
          return;
        }

        const session = response.session;

        // Customer details from DB
        setCustomerData({
          name: session.customer_name || "Unknown",
          phone: session.customer_phone || "N/A",
          email: session.customer_email || "N/A",
          project: session.customer_project || "N/A",
        });

        // AI summary
        if (session.ai_summary) {
          if (Array.isArray(session.ai_summary)) {
            setAiSummary(session.ai_summary as string[]);
          } else {
            try {
              const parsed = JSON.parse(session.ai_summary as any);
              setAiSummary(Array.isArray(parsed) ? parsed : []);
            } catch {
              setAiSummary([]);
            }
          }
        }

        // Transcript
        if (session.ai_transcript) {
          setAiTranscript(session.ai_transcript as string);
        }

        // Call analytics
        if (session.analytics) {
          setAnalytics(session.analytics);
        }

        // Recording data - SECURITY: Use backend API for signed URLs
        if (session.recording) {
          const storagePath = session.recording.url || session.recording.storage_path;
          setRecordingPath(storagePath);

          try {
            const signedUrlResponse = await apiClient.post<{ ok: boolean; signedUrl: string }>('/recordings/signed-url', {
              storagePath,
            });

            if (signedUrlResponse.ok && signedUrlResponse.signedUrl) {
              setRecordingUrl(signedUrlResponse.signedUrl);
            }
          } catch (urlError) {
            console.error("Error getting signed URL:", urlError);
            // Fallback to public URL if signed URL fails
            const { data: urlData } = supabase.storage
              .from("call-recordings")
              .getPublicUrl(storagePath);

            if (urlData?.publicUrl) {
              setRecordingUrl(urlData.publicUrl);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching analytics/AI data:", error);
      }
    };

    fetchAnalytics();
  }, [roomId]);

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return `${hours}h ${remainingMins}m`;
    }
    return `${mins}m ${secs}s`;
  };


  if (!isHostView) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <CardTitle>Thanks for joining!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Your call has ended. The host will reach out with any next steps. You can
              safely close this page.
            </p>
            <Button className="w-full" onClick={() => navigate("/dashboard")}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderAnalyticsDuration = () => {
    if (!analytics?.duration_seconds) return "N/A";
    const totalSec = analytics.duration_seconds as number;
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-background p-6 relative">
      <div className="max-w-4xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">Call Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 font-medium mb-1">Customer</p>
                <p className="text-lg font-semibold text-gray-900">
                  {customerData?.name || callData?.customerData?.name || "Unknown"}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {customerData?.phone || callData?.customerData?.phone || "No phone"}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600 font-medium mb-1">Duration</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDuration(analytics?.duration_seconds)}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-600 font-medium mb-1">Recording</p>
                <p className="text-lg font-semibold text-gray-900">
                  {recordingUrl ? "Available" : "Processing"}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  View in call history
                </p>
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="notes">Call Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this call..."
                rows={3}
                className="mb-2"
              />
              {notes && (
                <div className="flex items-center gap-1.5 text-sm text-green-600 mt-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="font-medium">Auto-saved</span>
                </div>
              )}
            </div>

            {snapshots.length > 0 && (
              <div className="mb-6">
                <Label className="text-base mb-3 block">Call Snapshots ({snapshots.length})</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {snapshots.map((s) => (
                    <button
                      key={s.name}
                      onClick={() => setSelectedImage(s)}
                      className="rounded-lg border-2 border-gray-200 overflow-hidden bg-white hover:border-blue-400 transition-colors group cursor-pointer"
                    >
                      <img
                        src={s.url}
                        alt={s.name}
                        className="w-full h-32 object-cover"
                        loading="lazy"
                      />
                      <div className="p-2 bg-gray-50 group-hover:bg-blue-50 transition-colors">
                        <span className="text-xs text-gray-600 group-hover:text-blue-600">
                          View
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        {/* AI Summary + Transcript - Always show */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>AI Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {aiSummary && aiSummary.length > 0 ? (
              <>
                <div className="space-y-2 mb-4">
                  {aiSummary.map((point, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                        {index + 1}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>

                {aiTranscript && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">View Full Transcript</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh]">
                      <DialogHeader>
                        <DialogTitle>Call Transcript</DialogTitle>
                        <DialogDescription>
                          Complete conversation transcript
                        </DialogDescription>
                      </DialogHeader>
                      <ScrollArea className="h-[60vh] pr-4">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {aiTranscript}
                        </div>
                      </ScrollArea>
                    </DialogContent>
                  </Dialog>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground mb-4 italic">
                AI summary is being generated. Please check back in 5 minutes.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button onClick={() => navigate("/dashboard")}>
            Return to Dashboard
          </Button>
        </div>
      </div>

      {/* Video Player Modal */}
      {recordingUrl && customerData && (
        <VideoPlayerModal
          open={showVideoPlayer}
          onOpenChange={setShowVideoPlayer}
          videoUrl={recordingUrl}
          storagePath={recordingPath || undefined}
          callDetails={{
            customerName: customerData.name,
            customerEmail: customerData.email !== "N/A" ? customerData.email : undefined,
            customerPhone: customerData.phone !== "N/A" ? customerData.phone : undefined,
            startTime: callData?.startTime,
            duration: callData?.durationMinutes,
            type: callData?.type,
            notes: notes || undefined,
          }}
        />
      )}

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          open={!!selectedImage}
          onOpenChange={(open) => !open && setSelectedImage(null)}
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
        />
      )}
    </div>
  );
}
